<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content">
                          <h4 class="nk-block-title">{{ $t('dm_mastr_title') }}</h4>
                        </div>
                      </div>

                      <!-- Import Section -->
                      <div class="card card-bordered mb-4" v-if="canDmManage">
                        <div class="card-inner">
                          <h5 class="card-title">{{ $t('dm_mastr_section_csv_import') }}</h5>
                          <div class="row">
                            <div class="col-sm-3">
                              <div class="form-group">
                                <label class="form-label">{{ $t('dm_common_col_system_type') }}</label>
                                <select class="form-control" v-model="importTyp">
                                  <option value="wea">WEA</option>
                                  <option value="pv">PV</option>
                                  <option value="bat">BAT</option>
                                </select>
                              </div>
                            </div>
                            <div class="col-sm-6">
                              <div class="form-group">
                                <label class="form-label">{{ $t('dm_mastr_label_csv_file') }}</label>
                                <input type="file" class="form-control" accept=".csv" @change="onCsvFileSelected" ref="csvInput" />
                              </div>
                            </div>
                            <div class="col-sm-3 d-flex align-items-end">
                              <button class="btn btn-primary" @click="importCsv" :disabled="importing">
                                <em class="icon ni ni-upload"></em> {{ $t('dm_mastr_btn_import') }}
                              </button>
                            </div>
                          </div>
                          <div class="form-group mt-2">
                            <label class="form-label">{{ $t('dm_mastr_label_csv_paste') }}</label>
                            <textarea class="form-control" rows="4" v-model="csvText" :placeholder="$t('dm_mastr_placeholder_csv')"></textarea>
                          </div>
                          <div class="alert alert-danger mt-2" v-if="importError">{{ importError }}</div>
                          <div class="alert alert-success mt-2" v-if="importSuccess">{{ importSuccess }}</div>
                        </div>
                      </div>

                      <!-- Status Tabs -->
                      <ul class="nav nav-tabs mb-3">
                        <li class="nav-item" v-for="tab in statusTabs" :key="tab.value">
                          <a class="nav-link" :class="{ active: statusFilter === tab.value }" href="#" @click.prevent="setStatusFilter(tab.value)">
                            {{ tab.label }}
                            <span class="badge badge-pill ml-1" :class="tab.badgeClass">{{ countByStatus(tab.value) }}</span>
                          </a>
                        </li>
                      </ul>

                      <div class="form-control-wrap mb-3">
                        <input type="text" class="form-control" :placeholder="$t('dm_common_quick_filter')" v-model="quickFilterText" />
                      </div>

                      <div class="card card-bordered">
                        <div style="overflow-x: auto;">
                          <table class="dm-table">
                            <thead>
                              <tr>
                                <th v-for="col in tableColumns" :key="col.field" @click="toggleSort(col.field)" class="dm-table-th">
                                  {{ col.label }}
                                  <span v-if="sortField === col.field" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="row in filteredData" :key="row._id" @click="onRowClicked(row)" class="dm-table-row">
                                <td v-for="col in tableColumns" :key="col.field">
                                  <span v-if="col.field === 'status'" class="badge" :class="statusBadgeClass(row.status)">{{ statusLabel(row.status) }}</span>
                                  <span v-else>{{ getCellValue(row, col) }}</span>
                                </td>
                              </tr>
                              <tr v-if="filteredData.length === 0">
                                <td :colspan="tableColumns.length" class="text-center text-muted py-4">{{ $t('dm_common_no_entries_found') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Detail/Accept Modal -->
                  <div class="modal fade" tabindex="-1" id="modalMastr">
                    <div class="modal-dialog modal-lg" role="document">
                      <div class="modal-content">
                        <a href="#" class="close" data-dismiss="modal" aria-label="Close">
                          <em class="icon ni ni-cross"></em>
                        </a>
                        <div class="modal-header">
                          <h5 class="modal-title">{{ $t('dm_mastr_modal_entry_title') }}</h5>
                          <span class="badge ml-2" :class="statusBadgeClass(selectedEntry && selectedEntry.status)">{{ statusLabel(selectedEntry && selectedEntry.status) }}</span>
                        </div>
                        <div class="modal-body">
                          <div class="alert alert-danger" v-if="modalError">{{ modalError }}</div>

                          <!-- Summary info -->
                          <div class="row mb-3" v-if="selectedEntry">
                            <div class="col-sm-4">
                              <div class="form-group">
                                <label class="form-label text-muted small">{{ $t('dm_common_col_serial_number') }}</label>
                                <div class="fw-bold">{{ selectedEntry.seriennummer || '-' }}</div>
                              </div>
                            </div>
                            <div class="col-sm-4">
                              <div class="form-group">
                                <label class="form-label text-muted small">{{ $t('dm_common_col_manufacturer') }}</label>
                                <div>{{ selectedEntry.hersteller || '-' }}</div>
                              </div>
                            </div>
                            <div class="col-sm-4">
                              <div class="form-group">
                                <label class="form-label text-muted small">{{ $t('dm_mastr_label_type_system') }}</label>
                                <div>{{ selectedEntry.typ || '-' }} / {{ selectedEntry.anlage_typ || '-' }}</div>
                              </div>
                            </div>
                          </div>

                          <!-- Anlage mapping for accept -->
                          <div v-if="selectedEntry && selectedEntry.status === 'vorgeschlagen' && canDmManage" class="card card-bordered mb-3">
                            <div class="card-inner py-3">
                              <h6 class="overline-title overline-title-alt mb-2">{{ $t('dm_mastr_section_assign_system') }}</h6>
                              <div class="form-group">
                                <label class="form-label">{{ $t('dm_mastr_label_link_existing') }}</label>
                                <select class="form-control" v-model="selectedAnlageId">
                                  <option value="">{{ $t('dm_mastr_new_system_create') }}</option>
                                  <option v-for="a in anlagenList" :key="a._id" :value="a._id">{{ a.seriennummer }} - {{ a.anlagendaten && a.anlagendaten.hersteller || '' }} {{ a.typ || '' }}</option>
                                </select>
                              </div>
                            </div>
                          </div>

                          <!-- Raw data -->
                          <div v-if="selectedEntry && selectedEntry.data && Object.keys(selectedEntry.data).length > 0">
                            <h6 class="overline-title overline-title-alt mb-2">{{ $t('dm_mastr_section_raw_data') }}</h6>
                            <div style="max-height: 300px; overflow-y: auto;">
                              <table class="table table-sm table-striped">
                                <tbody>
                                  <tr v-for="(value, key) in selectedEntry.data" :key="key">
                                    <td class="mastr-key"><strong>{{ formatDataKey(key) }}</strong></td>
                                    <td>{{ value }}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <div class="modal-footer d-flex justify-content-between">
                          <div>
                            <button v-if="canDmManage && selectedEntry && selectedEntry.status === 'vorgeschlagen'" class="btn btn-sm btn-outline-danger" @click="rejectEntry">
                              <em class="icon ni ni-trash"></em> {{ $t('dm_mastr_btn_reject') }}
                            </button>
                          </div>
                          <div>
                            <button v-if="canDmManage && selectedEntry && selectedEntry.status === 'vorgeschlagen'" class="btn btn-sm btn-success" @click="acceptEntry">
                              <em class="icon ni ni-check"></em> {{ $t('dm_mastr_btn_accept') }}
                            </button>
                            <button type="button" class="btn btn-sm btn-secondary ml-2" data-dismiss="modal">{{ $t('dm_common_btn_close') }}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmTableMixin from "@/mixins/dm-table-mixin";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { mastrImport, mastrList, mastrAccept, mastrReject } from "@/services/dm.service";

export default {
  name: "DmMastr",
  components: { Header, Navigation, Footer },
  mixins: [dmTableMixin, dmPermissionsMixin],
  data() {
    return {
      fullLoader: null,
      allEntries: [],
      importTyp: "wea",
      csvText: "",
      csvFile: null,
      importing: false,
      importError: "",
      importSuccess: "",
      selectedEntry: null,
      selectedAnlageId: "",
      modalError: "",
      statusFilter: "vorgeschlagen",
    };
  },
  computed: {
    statusTabs() {
      return [
        { value: "vorgeschlagen", label: this.$t('dm_mastr_tab_proposed'), badgeClass: "badge-warning" },
        { value: "uebernommen", label: this.$t('dm_mastr_tab_accepted'), badgeClass: "badge-success" },
        { value: "abgelehnt", label: this.$t('dm_mastr_tab_rejected'), badgeClass: "badge-secondary" },
      ];
    },
    tableColumns() {
      return [
        { label: this.$t('dm_common_col_status'), field: "status" },
        { label: this.$t('dm_common_col_serial_number'), field: "seriennummer" },
        { label: this.$t('dm_common_col_manufacturer'), field: "hersteller" },
        { label: this.$t('dm_common_col_type'), field: "typ" },
        { label: this.$t('dm_common_col_system_type'), field: "anlage_typ" },
        { label: this.$t('dm_mastr_col_imported_at'), field: "created_at", formatter: function(val) { return val ? new Date(val).toLocaleDateString("de-DE") : ""; } },
      ];
    },
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page == "setup";
    },
    mastrEntries() {
      return this.allEntries.filter((e) => e.status === this.statusFilter);
    },
    filteredData() {
      return this.filterAndSort(this.mastrEntries, this.tableColumns);
    },
    anlagenList() {
      return this.$store.state.dm_anlagen || [];
    },
  },
  created() {
    this.loadMastrList();
  },
  methods: {
    loadMastrList() {
      this.showLoader();
      mastrList({})
        .then((d) => {
          this.hideLoader();
          if (d.data && d.data.list) this.allEntries = d.data.list;
        })
        .catch((e) => {
          this.hideLoader();
          this.$store.dispatch("api_error", e);
        });
    },
    setStatusFilter(status) {
      this.statusFilter = status;
    },
    countByStatus(status) {
      return this.allEntries.filter((e) => e.status === status).length;
    },
    statusLabel(status) {
      if (status === "vorgeschlagen") return this.$t('dm_mastr_tab_proposed');
      if (status === "uebernommen") return this.$t('dm_mastr_tab_accepted');
      if (status === "abgelehnt") return this.$t('dm_mastr_tab_rejected');
      return status || "";
    },
    statusBadgeClass(status) {
      if (status === "vorgeschlagen") return "badge-warning";
      if (status === "uebernommen") return "badge-success";
      if (status === "abgelehnt") return "badge-secondary";
      return "badge-light";
    },
    formatDataKey(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, function(c) { return c.toUpperCase(); });
    },
    onCsvFileSelected(event) {
      this.csvFile = event.target.files[0] || null;
    },
    importCsv() {
      this.importError = "";
      this.importSuccess = "";
      var csvData = this.csvText;
      if (this.csvFile) {
        var reader = new FileReader();
        reader.onload = (e) => {
          this.doImport(e.target.result);
        };
        reader.readAsText(this.csvFile);
      } else if (csvData) {
        this.doImport(csvData);
      } else {
        this.importError = this.$t('dm_mastr_error_csv_required');
      }
    },
    doImport(csvData) {
      this.importing = true;
      mastrImport(csvData, this.importTyp)
        .then((d) => {
          this.importing = false;
          var err = this.getApiError(d); if (err) {
            this.importError = err;
          } else {
            this.importSuccess = this.$t('dm_mastr_success_import_count') + ": " + (d.data.count || 0) + " " + this.$t('dm_mastr_entries');
            this.loadMastrList();
          }
        })
        .catch(() => {
          this.importing = false;
          this.importError = this.$t('dm_mastr_error_import_failed');
        });
    },
    onRowClicked(row) {
      if (row) {
        this.selectedEntry = row;
        this.selectedAnlageId = "";
        this.modalError = "";
        $("#modalMastr").modal("show");
      }
    },
    acceptEntry() {
      if (!this.selectedEntry) return;
      this.modalError = "";
      var anlageId = this.selectedAnlageId || null;
      mastrAccept(this.selectedEntry._id, anlageId, null)
        .then((d) => {
          var err = this.getApiError(d);
          if (err) {
            this.modalError = err;
            return;
          }
          $("#modalMastr").modal("hide");
          this.$swal(this.$t('dm_mastr_success_accepted'), this.$t('dm_mastr_success_accepted_msg'), "success");
          this.loadMastrList();
        })
        .catch((e) => {
          this.modalError = this.$t('dm_mastr_error_accept_failed');
          this.$store.dispatch("api_error", e);
        });
    },
    rejectEntry() {
      if (!this.selectedEntry) return;
      this.$swal({
        title: this.$t('dm_mastr_confirm_reject_title'),
        text: this.$t('dm_mastr_confirm_reject_text'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: this.$t('dm_mastr_btn_reject_yes'),
        cancelButtonText: this.$t('dm_common_btn_cancel'),
      }).then((result) => {
        if (result.isConfirmed || result.value) {
          this.modalError = "";
          mastrReject(this.selectedEntry._id)
            .then((d) => {
              var err = this.getApiError(d);
              if (err) {
                this.modalError = err;
                return;
              }
              $("#modalMastr").modal("hide");
              this.loadMastrList();
            })
            .catch((e) => {
              this.modalError = this.$t('dm_mastr_error_reject_failed');
              this.$store.dispatch("api_error", e);
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.dm-table {
  min-width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.dm-table th,
.dm-table td {
  white-space: nowrap;
}

.dm-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e2e8f0;
}

.dm-table-th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.dm-table-th:hover {
  color: #334155;
}

.dm-sort-icon {
  font-size: 10px;
  margin-left: 4px;
  color: #E8751A;
}

.dm-table tbody td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.dm-table-row {
  cursor: pointer;
  transition: background 0.15s;
}

.dm-table-row:hover {
  background: #f8fafc;
}

.nav-tabs .nav-link {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

.nav-tabs .nav-link.active {
  color: #E8751A;
  border-bottom-color: #E8751A;
}

.badge-warning {
  background: #FFA726;
  color: #fff;
}

.badge-success {
  background: #24c45f;
  color: #fff;
}

.badge-secondary {
  background: #E5E5E3;
  color: #555;
}

.mastr-key {
  width: 40%;
  color: #64748b;
}

.fw-bold {
  font-weight: 600;
}

.overline-title-alt {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 600;
}
</style>
